import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context.js';
import { apiFetch } from '../lib/api.js';
import { formatUserName } from '@crm98/shared';

interface SearchResults {
  orgs: { id: string; name: string; slug: string; status: string }[];
  leads: { id: string; name: string; email: string; org_id: string; org_name: string }[];
  users: { id: string; email: string; role: string; org_id: string; org_name: string }[];
}

function useHealthDot() {
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const [deadLetters, setDeadLetters] = useState(0);
  useEffect(() => {
    const check = async () => {
      try {
        const d = await apiFetch<{ worker: { healthy: boolean }; queue: { deadLetters: number } }>('/admin/health');
        setHealthy(d.worker.healthy);
        setDeadLetters(d.queue.deadLetters);
      } catch { setHealthy(false); }
    };
    check();
    const timer = setInterval(check, 30_000);
    return () => clearInterval(timer);
  }, []);
  return { healthy, deadLetters };
}

export function AdminLayout() {
  const { logout, userEmail, userFirstName, userLastName, userRole } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const { healthy, deadLetters } = useHealthDot();

  const navItems = [
    { to: '/admin/orgs', label: 'Accounts' },
    { to: '/admin/metrics', label: 'Metrics' },
    { to: '/admin/settings', label: 'Settings' },
    { to: '/admin/migrations', label: 'Migrations' },
    { to: '/admin/system', label: 'System' },
  ];

  // Global search with debounce
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults(null); return; }
    const timer = setTimeout(async () => {
      try {
        const d = await apiFetch<SearchResults>(`/admin/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(d);
        setShowSearch(true);
      } catch { /* */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSelect = (type: string, id: string) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults(null);
    if (type === 'org') navigate(`/admin/orgs?view=${id}`);
    // leads and users navigate to their org
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">97</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-semibold text-gray-900 text-sm leading-tight">CRM98</h1>
              <div className="flex items-center gap-1.5">
                <p className="text-xs text-gray-400 leading-tight">Admin</p>
                {healthy !== null && (
                  <div className={`w-2 h-2 rounded-full ${healthy ? 'bg-green-500' : 'bg-red-500'}`} title={healthy ? 'System healthy' : `System issue${deadLetters > 0 ? ` (${deadLetters} dead letters)` : ''}`} />
                )}
              </div>
            </div>
          </div>

          {/* Global search */}
          <div className="relative flex-1 max-w-sm">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => searchResults && setShowSearch(true)}
              placeholder="Search accounts, leads, users..."
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>

            {showSearch && searchResults && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowSearch(false)} />
                <div className="absolute top-10 left-0 right-0 bg-white rounded-xl shadow-lg border border-gray-200 z-40 max-h-80 overflow-auto">
                  {searchResults.orgs.length === 0 && searchResults.leads.length === 0 && searchResults.users.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500 text-center">No results found</p>
                  ) : (
                    <>
                      {searchResults.orgs.length > 0 && (
                        <div>
                          <p className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase">Accounts</p>
                          {searchResults.orgs.map(o => (
                            <button key={o.id} onClick={() => handleSearchSelect('org', o.id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between">
                              <span className="text-sm text-gray-900">{o.name}</span>
                              <span className="text-xs text-gray-400">{o.status}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {searchResults.leads.length > 0 && (
                        <div>
                          <p className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase">Leads</p>
                          {searchResults.leads.map(l => (
                            <button key={l.id} onClick={() => handleSearchSelect('org', l.org_id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50">
                              <span className="text-sm text-gray-900">{l.name}</span>
                              <span className="text-xs text-gray-500 ml-2">{l.email}</span>
                              <span className="text-xs text-gray-400 ml-2">{l.org_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {searchResults.users.length > 0 && (
                        <div>
                          <p className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase">Users</p>
                          {searchResults.users.map(u => (
                            <button key={u.id} onClick={() => handleSearchSelect('org', u.org_id)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between">
                              <span className="text-sm text-gray-900">{u.email}</span>
                              <span className="text-xs text-gray-400">{u.role} | {u.org_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 flex-shrink-0">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                    isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User menu */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-10 bg-white rounded-xl shadow-lg border border-gray-200 z-50 w-56 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{formatUserName({ firstName: userFirstName, lastName: userLastName, email: userEmail ?? '' })}</p>
                    {userRole && (
                      <p className="text-xs text-gray-500 mt-0.5 capitalize">{userRole.replace(/_/g, ' ')}</p>
                    )}
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => { logout(); setShowMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
